// src/client/index.tsx
// Last updated: Aug 8 2025

import "./styles.css";

import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// The type of messages we'll be receiving from the server
import type { OutgoingMessage } from "../shared";
import type { LegacyRef } from "react";

function App() {
  // A reference to the canvas element where we'll render the globe
  const canvasRef = useRef<HTMLCanvasElement>();
  // The number of current live connections
  const [liveCounter, setLiveCounter] = useState(0);
  // Total number of unique visits from the database
  const [totalVisits, setTotalVisits] = useState(0);
  // A map of marker IDs to their positions
  // Note that we use a ref because the globe's `onRender` callback
  // is called on every animation frame, and we don't want to re-render
  // the component on every frame.
  const positions = useRef<
    Map<
      string,
      {
        location: [number, number];
        size: number;
      }
    >
  >(new Map());
  
  // Connect to the PartyServer server
  const socket = usePartySocket({
    room: "default",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;
      if (message.type === "add-marker") {
        // Add the marker to our map
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: message.position.id === socket.id ? 0.04 : 0.01,
        });
        // Update the live counter
        setLiveCounter((c) => c + 1);
      } else if (message.type === "remove-marker") {
        // Remove the marker from our map
        positions.current.delete(message.id);
        // Update the live counter
        setLiveCounter((c) => c - 1);
      } else if (message.type === "total-visits") {
        // Update the total visits counter
        setTotalVisits(message.count);
      } else if (message.type === "stored-markers") {
        // Add stored markers from database (these are historical, not live)
        message.markers.forEach((marker: any) => {
          positions.current.set(`stored-${marker.id}`, {
            location: [marker.lat, marker.lng],
            size: 0.025, // More visible size for historical markers
          });
        });
      }
    },
  });

  useEffect(() => {
    // The angle of rotation of the globe
    // We'll update this on every frame to make the globe spin
    let phi = 0;

    const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0,
      dark: 1,
      diffuse: 0.8,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.1, 0.8, 0.1], // Green for live connections
      glowColor: [0.2, 0.2, 0.2],
      markers: [],
      opacity: 0.7,
      onRender: (state) => {
        // Called on every animation frame.
        // `state` will be an empty object, return updated params.

        // Get the current positions from our map
        state.markers = [...positions.current.values()];

        // Rotate the globe
        state.phi = phi;
        phi += 0.005;
      },
    });

    return () => {
      globe.destroy();
    };
  }, []);

  return (
    <div className="App">
      <h1>Where they at?</h1>
      
      <div style={{ marginBottom: '20px' }}>
        {liveCounter > 0 && (
          <p>
            <b>{liveCounter}</b> {liveCounter === 1 ? "person" : "people"} connected right now.
          </p>
        )}
        
        {totalVisits > 0 && (
          <p>
            <b>{totalVisits}</b> unique {totalVisits === 1 ? "visitor" : "visitors"} total.
          </p>
        )}
        
        {liveCounter === 0 && totalVisits === 0 && <p>&nbsp;</p>}
      </div>

      {/* The canvas where we'll render the globe */}
      <canvas
        ref={canvasRef as LegacyRef<HTMLCanvasElement>}
        style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: 1 }}
      />

      <p>
        Powered by love.
      </p>
      
      <p>
        <a 
          href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#888', textDecoration: 'underline', fontSize: '14px' }}
        >
          Click here to learn more
        </a>
      </p>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("root")!).render(<App />);